import React, { useEffect, useRef, useState } from 'react';
import FormSingleResult from '../FormsResultsTable/FormSingleResult';
import { useMessage } from '../../hooks/useMessage';
import { FormsProps, FormsResultsDTO } from '../../types/forms';
import { deleteFormResponse, getFormsRows } from '../../api/forms.api';
import Loading from '../loading/Loading';
import { Dialog, DialogContent, Typography } from '@mui/material';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { transformFormResponses } from '../../utils/forms.utils';
import DataUpdateForm from './DataUpdateForm';
import { useEvents } from '../../hooks/useEvents';
import { useError } from '../../context/error-context';
const ViewSingleRowForm = ({ formId, linkedFormId, formAllFields, formType, skin, formName }: FormsProps) => {
    const { showMessage, MessageContainer } = useMessage();
    const [ results, setResults ] = useState<FormsResultsDTO['responses']>([]);
    const [ resultsLoading, setResultsLoading ] = useState(true);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ removeLoading, setRemoveLoading ] = useState(false);
    const responseIdsToRemove = useRef<(string | number)[]>([]);
    const [ editModalOpen, setEditModalOpen ] = useState(false);
    const [ editingResponseId, setEditingResponseId ] = useState('');
    const { subscribe } = useEvents();
    const { showError } = useError();
    let realFormId = +formId;
    if (linkedFormId) {
        realFormId = linkedFormId;
    }
    useEffect(() => {
        setResultsLoading(true);
        getFormsRows(realFormId, formId)
        .then(response => {
            setResults(response.responses);
        })
        .catch((err) => {
            showError({
                code: err.response?.status || 500,
                message: 'API Error',
                description: 'Failed to fetch data',
                showRefresh: true,
            });
        })
        .finally(() => setResultsLoading(false));
    }, [ realFormId, formId, showError ]);
    useEffect(() => {
        return subscribe(`REFRESH_FORMS_${realFormId}`, () => {
            setResultsLoading(true);
            getFormsRows(realFormId, formId)
            .then(response => {
                setResults(response.responses);
            })
            .catch((err) => {
                showError({
                    code: err.response?.status || 500,
                    message: 'API Error',
                    description: 'Failed to fetch data',
                    showRefresh: true,
                });
            })
            .finally(() => setResultsLoading(false));
        });
    }, [ realFormId, formId, subscribe, showError ]);
    const onDeleteConfirm = async () => {
        if (responseIdsToRemove.current.length === 0 || !realFormId) {
            return;
        }
        setRemoveLoading(true);
        Promise.all(
            responseIdsToRemove.current.map((id) => deleteFormResponse(realFormId, id)),
        )
        .then(() => {
            showMessage('The response(s) has been deleted.', 'success');
            const filteredResults = results.filter(item => !responseIdsToRemove.current.includes(item.response_id));
            setResults(filteredResults);
            responseIdsToRemove.current = [];
        })
        .catch(() => {
            showMessage('Couldn\'t remove the record(s). Please try again later.', 'error');
        })
        .finally(() => {
            setConfirmModalOpen(false);
            setRemoveLoading(false);
        });
    };
    const onResponseEdit = (responseId: string | number) => {
        setEditingResponseId(responseId + '');
        setEditModalOpen(true);
    };
    const onAfterUpdate = (updatedResult: any) => {
        setResults(results.map(result => {
            if (+result.response_id === updatedResult.response_id) {
                return {
                    ...result,
                    response: updatedResult.data,
                };
            }
            return result;
        }));
    };
    const onDeleteFormResponse = (responseId: string | number) => {
        setConfirmModalOpen(true);
        responseIdsToRemove.current = [ responseId ];
    };
    return (
        <div>
            {resultsLoading ? <Loading/> : (
                <div style={{ margin: '16px', maxWidth: '98%', width: 'fit-content' }}>
                    <div>
                        <Typography variant="h4" gutterBottom>
                            {formName || 'Submitted Results'}
                        </Typography>
                    </div>
                    {formAllFields.length > 0 ? (
                        <FormSingleResult
                            config={{ fields: formAllFields.filter(item => !item.hidden) }}
                            data={transformFormResponses(results)}
                            onEdit={onResponseEdit}
                            onDelete={onDeleteFormResponse}
                            hasActions
                            skin={skin}
                        />
                    ) : (
                        <Typography variant="h6">
                            Couldn't load the results.
                        </Typography>
                    )}
                </div>
            )}
            <ConfirmModal
                isLoading={removeLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />
            {editModalOpen && editingResponseId && (
                <Dialog
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    maxWidth={false}
                >
                    <DialogContent
                        sx={{
                            minWidth: 300,
                            width: 'fit-content',
                        }}
                    >
                        <DataUpdateForm
                            formId={realFormId + ''}
                            formType={formType}
                            formAllFields={formAllFields}
                            skin={skin}
                            responseId={editingResponseId}
                            onCancel={() => setEditModalOpen(false)}
                            onAfterUpdate={onAfterUpdate}
                        />
                    </DialogContent>
                </Dialog>
            )}
            <MessageContainer/>
        </div>
    );
};
export default ViewSingleRowForm;